/**
 * ハードウェア風の縦レバートグルスイッチ（TWIN / GOLD / CLIP / KAITLYN 等の ON-OFF 用）。
 *
 * グローバルの Toggle はライトテーマの pill 型で、ダークなフェイスプレート
 * 上の機材風レバーとは用途・見た目が別物のため、ページ局所で実装する
 * （汎用 Toggle への variant 追加は実験ページ 1 箇所のための肥大化になる）。
 */

import React from "react";
import styled from "styled-components";

import { PANEL, PANEL_FONT } from "../palette";

const Root = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid ${PANEL.focus};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const Label = styled.span`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 11px;
  letter-spacing: 0.12em;
  color: ${PANEL.engraving};
  text-transform: uppercase;
`;

const Plate = styled.span`
  width: 34px;
  height: 58px;
  border-radius: 6px;
  background: linear-gradient(180deg, #10161d 0%, #1c242e 100%);
  box-shadow:
    inset 0 2px 5px rgba(0, 0, 0, 0.8),
    0 1px 0 rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const Lever = styled.span<{ $on: boolean; $color: string }>`
  width: 14px;
  height: 30px;
  border-radius: 7px;
  background: linear-gradient(
    180deg,
    #f5f8fa 0%,
    #c9d2da 55%,
    #97a4b0 100%
  );
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.6);
  transform: translateY(${({ $on }) => ($on ? "-9px" : "9px")});
  transition: transform 0.12s ease-out;
  position: relative;

  /* レバー先端のカラーチップ（ON 位置で点灯） */
  &::after {
    content: "";
    position: absolute;
    left: 2px;
    right: 2px;
    top: 2px;
    height: 8px;
    border-radius: 5px;
    background: ${({ $on, $color }) => ($on ? $color : "#5a646e")};
    box-shadow: ${({ $on, $color }) =>
      $on ? `0 0 8px ${$color}` : "none"};
    transition: background 0.12s ease-out;
  }
`;

const StateText = styled.span<{ $on: boolean }>`
  font-family: ${PANEL_FONT};
  font-weight: 800;
  font-size: 10px;
  letter-spacing: 0.14em;
  color: ${({ $on }) => ($on ? PANEL.engravingStrong : PANEL.tick)};
`;

export interface ToggleSwitchProps {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  color: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  label,
  value,
  onChange,
  color,
}) => (
  <Root
    type="button"
    role="switch"
    aria-checked={value}
    aria-label={label}
    onClick={() => onChange(!value)}
  >
    <Label>{label}</Label>
    <Plate>
      <Lever $on={value} $color={color} />
    </Plate>
    <StateText $on={value}>{value ? "ON" : "OFF"}</StateText>
  </Root>
);

export default ToggleSwitch;
